import React from 'react';
import { ResultComponentProps } from '../../common/types';
import BaseResultDisplay from '../../common/BaseResultDisplay';
import { Typography, Table, Tag, Tooltip, Divider, Card, Space } from 'antd';
import * as d3 from 'd3';

const { Title, Text, Paragraph } = Typography;

// 定义区域类型
type RegionType = 'transmembrane helix' | 'inside' | 'outside';

// 区域类型颜色映射
const regionColors: Record<RegionType, string> = {
  'transmembrane helix': '#ff4d4f',  // 红色
  'inside': '#1890ff',               // 蓝色
  'outside': '#52c41a',              // 绿色
};

// 定义区域带接口
interface RegionBand {
  start: number;
  end: number;
  type: RegionType;
  color: string;
}

// 线图数据点接口
interface DataPoint {
  x: number;
  y: number;
}

const parseSequenceInfo = (rawSequence?: string) => {
  if (!rawSequence) {
    return { id: '', sequence: '' };
  }

  const trimmed = rawSequence.trim();
  if (trimmed.startsWith('>')) {
    const lines = trimmed.split(/\r?\n/);
    const header = lines.shift()?.replace(/^>/, '').trim() || '';
    const seq = lines.join('').replace(/\s+/g, '').trim();
    return { id: header, sequence: seq };
  }

  return { id: '', sequence: trimmed.replace(/\s+/g, '') };
};

// 单个序列结果组件
const TMSequenceResult: React.FC<{
  result: any;
  artifacts: any;
  sequenceId: string;
  rawSequence?: string;
}> = ({ result, artifacts, sequenceId, rawSequence }) => {
  const chartRef = React.useRef<HTMLDivElement>(null);
  
  const { id: parsedId, sequence: parsedSequence } = React.useMemo(() => parseSequenceInfo(rawSequence), [rawSequence]);
  const displayId = sequenceId || parsedId;
  const displaySequence = parsedSequence || result.sequence || '';
  
  // 从artifacts中获取该序列的绘图数据
  const plotData = artifacts.plot_data_map?.[sequenceId] || artifacts.plot_data || {};
  const xValues = plotData.x_values || [];
  const series = plotData.series || [];
  const summary = result.summary || [];
  const annotation = result.annotation || '';
  const tmRegionsCount = result.tm_regions_count || 0;
  
  // 从annotation推断序列（如果没有直接提供）
  const sequence = annotation || displaySequence;
  
  // 使用D3创建线图
  React.useEffect(() => {
    if (chartRef.current && xValues.length > 0 && series.length > 0) {
      createLineChart();
    }
  }, [xValues, series]);
  
  // 创建D3线图的函数（复用主组件的逻辑）
  const createLineChart = () => {
    if (!chartRef.current) return;
    
    // 清除旧图表
    d3.select(chartRef.current).selectAll("*").remove();
    
    if (xValues.length === 0 || series.length === 0) {
      d3.select(chartRef.current)
        .append("text")
        .attr("x", chartRef.current.clientWidth / 2)
        .attr("y", 150)
        .attr("text-anchor", "middle")
        .text("无数据可显示");
      return;
    }
    
    const margin = { top: 40, right: 100, bottom: 60, left: 60 };
    const width = chartRef.current.clientWidth - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;
    
    const svg = d3.select(chartRef.current)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);
    
    const x = d3.scaleLinear()
      .domain([Math.min(...xValues), Math.max(...xValues)])
      .range([0, width]);
    
    svg.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x).ticks(10));
    
    svg.append("text")
      .attr("transform", `translate(${width/2}, ${height + 40})`)
      .style("text-anchor", "middle")
      .text("残基位置");
    
    const y = d3.scaleLinear()
      .domain([0, 1])
      .range([height, 0]);
    
    svg.append("g")
      .call(d3.axisLeft(y));
    
    svg.append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", -40)
      .attr("x", -height/2)
      .style("text-anchor", "middle")
      .text("概率分值");
    
    series.forEach((s: any, i: number) => {
      if (!s.data || s.data.length === 0) return;
      
      const points: DataPoint[] = xValues.map((x: number, j: number) => ({
        x: x,
        y: s.data[j] || 0
      }));
      
      const line = d3.line<DataPoint>()
        .x(d => x(d.x))
        .y(d => y(d.y))
        .curve(d3.curveBasis);
      
      svg.append("path")
        .datum(points)
        .attr("fill", "none")
        .attr("stroke", s.color || d3.schemeCategory10[i % 10])
        .attr("stroke-width", 2)
        .attr("d", line);
    });
    
    const legend = svg.append("g")
      .attr("transform", `translate(${width + 10}, 0)`);
    
    series.forEach((s: any, i: number) => {
      const legendRow = legend.append("g")
        .attr("transform", `translate(0, ${i * 20})`);
      
      legendRow.append("rect")
        .attr("width", 10)
        .attr("height", 10)
        .attr("fill", s.color || d3.schemeCategory10[i % 10]);
      
      legendRow.append("text")
        .attr("x", 15)
        .attr("y", 9)
        .text(s.name || `系列 ${i+1}`)
        .style("font-size", "12px");
    });
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Card size="small">
        <Space direction="vertical" style={{ width: '100%' }}>
          {displayId && (
            <div>
              <Text strong>序列ID: </Text>
              <Text>{displayId}</Text>
            </div>
          )}
          <div>
            <Text strong>序列长度: </Text>
            <Text>{sequence.length} 氨基酸</Text>
          </div>
          <div>
            <Text strong>跨膜区域数量: </Text>
            <Text>{tmRegionsCount}</Text>
            {tmRegionsCount > 0 ? 
              <Tag color="green" style={{ marginLeft: 8 }}>跨膜蛋白</Tag> : 
              <Tag color="blue" style={{ marginLeft: 8 }}>非跨膜蛋白</Tag>
            }
          </div>
        </Space>
      </Card>

      {sequence && (
        <>
          <Title level={5}>序列注释</Title>
          <div style={{ 
            fontFamily: 'monospace', 
            lineHeight: '1.5', 
            whiteSpace: 'pre-wrap', 
            wordBreak: 'break-all',
            backgroundColor: '#f5f5f5',
            padding: 8,
            borderRadius: 4
          }}>
            {sequence.split('').map((aa: string, idx: number) => {
              const isTransmembrane = aa === 'M'; // 简化的跨膜区域标记
              return (
                <span
                  key={idx}
                  style={{
                    color: isTransmembrane ? '#ff4d4f' : '#000000',
                    fontWeight: isTransmembrane ? 'bold' : 'normal',
                  }}
                >
                  {aa}
                </span>
              );
            })}
          </div>
        </>
      )}

      {summary.length > 0 && (
        <>
          <Title level={5}>区域信息</Title>
          <Table 
            dataSource={summary} 
            columns={[
              {
                title: '区域类型',
                dataIndex: 'type',
                key: 'type',
                render: (text: string) => (
                  <Tag color={regionColors[text as RegionType] || 'default'}>
                    {text}
                  </Tag>
                )
              },
              {
                title: '起始位置',
                dataIndex: 'start',
                key: 'start',
              },
              {
                title: '结束位置',
                dataIndex: 'end',
                key: 'end',
              },
              {
                title: '长度',
                key: 'length',
                render: (_: any, record: any) => record.end - record.start + 1
              }
            ]}
            rowKey={(record, index) => `${record.type}-${record.start}-${record.end}-${index}`}
            pagination={false}
            size="small"
          />
        </>
      )}

      {xValues.length > 0 && series.length > 0 && (
        <>
          <Title level={5}>跨膜概率曲线</Title>
          <div ref={chartRef} style={{ width: '100%', height: '300px' }} />
        </>
      )}
    </Space>
  );
};

// 跨膜区预测结果展示组件
const TMPredictionResult: React.FC<ResultComponentProps> = ({
  data,
  title,
  onDeleteResult,
  multiResults,
  originalInput
}) => {
  const chartRef = React.useRef<HTMLDivElement>(null);
  
  // 如果没有数据，显示警告
  if (!data) {
    return (
      <BaseResultDisplay
        data={data}
        title={title || "跨膜区预测结果"}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
        showRawData={false}
      >
        <Text type="warning">无法显示跨膜区预测结果，数据为空。</Text>
      </BaseResultDisplay>
    );
  }

  const prepareUnifiedData = React.useMemo(() => {
    if (!data) return null;

    const resultsArray = Array.isArray(data.results)
      ? data.results
      : Array.isArray(data.batch)
        ? data.batch
        : null;

    if (!resultsArray) return null;

    const sequenceMap: Record<string, { id: string; sequence: string }> = {};

    const parseFasta = (input: string | undefined) => {
      if (!input) return [];
      const lines = input.trim().split(/\r?\n/);
      const entries: Array<{ id: string; sequence: string }> = [];
      let currentId = '';
      let currentSeq: string[] = [];

      const pushEntry = () => {
        if (!currentSeq.length) return;
        const sequence = currentSeq.join('').replace(/\s+/g, '');
        if (sequence) {
          entries.push({ id: currentId || '', sequence });
        }
      };

      lines.forEach(line => {
        const trimmed = line.trim();
        if (!trimmed) return;
        if (trimmed.startsWith('>')) {
          pushEntry();
          currentId = trimmed.substring(1).trim();
          currentSeq = [];
        } else {
          currentSeq.push(trimmed);
        }
      });
      pushEntry();
      return entries;
    };

    const fastaEntries = parseFasta(originalInput ?? '');
    fastaEntries.forEach(entry => {
      if (entry.sequence) {
        sequenceMap[entry.id || entry.sequence] = entry;
      }
    });

    const normalizedResults = resultsArray.map((item: any, index: number) => {
      const baseResult = item?.result || item?.data || item || {};
      const sequenceId = item?.sequence_id || item?.name || fastaEntries[index]?.id || `序列${index + 1}`;
      const matchedEntry = sequenceMap[sequenceId] || fastaEntries[index];

      const enrichedResult = {
        ...baseResult,
        sequence: baseResult.sequence || matchedEntry?.sequence || ''
      };

      return {
        ...item,
        sequence_id: sequenceId,
        result: enrichedResult,
        success: item?.success !== false,
        error: item?.error || null
      };
    });

    return {
      isUnified: true,
      results: normalizedResults,
      artifacts: data.artifacts || data.artifact || {},
      total_sequences: data.total_sequences ?? normalizedResults.length,
      successful_count: data.successful_count ?? normalizedResults.filter((r: any) => r.success !== false).length,
      failed_count: data.failed_count ?? normalizedResults.filter((r: any) => r.success === false).length
    };
  }, [data, originalInput]);

  const unifiedData = prepareUnifiedData;

  if (unifiedData) {
    return (
      <BaseResultDisplay
        data={data}
        title={title || "跨膜区预测结果"}
        onDeleteResult={onDeleteResult}
        multiResults={true}
        showRawData={false}
      >
        <Card style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text strong>总序列数: </Text>
              <Text>{unifiedData.total_sequences}</Text>
            </div>
            <div>
              <Text strong>成功预测: </Text>
              <Text style={{ color: '#52c41a' }}>{unifiedData.successful_count}</Text>
            </div>
            <div>
              <Text strong>失败预测: </Text>
              <Text style={{ color: '#ff4d4f' }}>{unifiedData.failed_count}</Text>
            </div>
          </Space>
        </Card>

        {unifiedData.results.map((result: any, index: number) => {
          const sequenceId = result.sequence_id || `序列${index + 1}`;

          return (
            <Card key={index} style={{ marginBottom: 16 }} title={`序列: ${sequenceId}`}>
              {result.success ? (
                <TMSequenceResult
                  result={result.result}
                  artifacts={unifiedData.artifacts}
                  sequenceId={sequenceId}
                  rawSequence={result.result?.sequence || result.raw_sequence}
                />
              ) : (
                <Text type="danger">预测失败: {result.error}</Text>
              )}
            </Card>
          );
        })}
      </BaseResultDisplay>
    );
  }

  // 单序列处理（兼容旧格式）
  const processedData = data || {};
  const plotData = processedData.plot_data || {};
  const xValues = plotData.x_values || [];
  const series = plotData.series || [];
  const summary = processedData.summary || [];
  const rawSequenceInfo = React.useMemo(() => parseSequenceInfo(processedData.sequence), [processedData.sequence]);
  const sequence = processedData.annotation || rawSequenceInfo.sequence;
  const sequenceId = processedData.sequence_id || rawSequenceInfo.id || processedData.name || '';
  
  // 使用D3创建线图
  React.useEffect(() => {
    if (chartRef.current && xValues.length > 0 && series.length > 0) {
      createLineChart();
    }
  }, [data, xValues, series]);
  
  // 创建D3线图
  const createLineChart = () => {
    if (!chartRef.current) return;
    
    // 清除旧图表
    d3.select(chartRef.current).selectAll("*").remove();
    
    // 如果没有数据，不绘制图表
    if (xValues.length === 0 || series.length === 0) {
      d3.select(chartRef.current)
        .append("text")
        .attr("x", chartRef.current.clientWidth / 2)
        .attr("y", 150)
        .attr("text-anchor", "middle")
        .text("无数据可显示");
      return;
    }
    
    // 设置图表尺寸和边距
    const margin = { top: 40, right: 100, bottom: 60, left: 60 };
    const width = chartRef.current.clientWidth - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;
    
    // 创建SVG容器
    const svg = d3.select(chartRef.current)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);
    
    // 创建X轴比例尺
    const x = d3.scaleLinear()
      .domain([Math.min(...xValues), Math.max(...xValues)])
      .range([0, width]);
    
    // 添加X轴
    svg.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x).ticks(10));
    
    // 添加X轴标签
    svg.append("text")
      .attr("transform", `translate(${width/2}, ${height + 40})`)
      .style("text-anchor", "middle")
      .text("残基位置");
    
    // 创建Y轴比例尺
    const y = d3.scaleLinear()
      .domain([0, 1])  // 概率值范围为0-1
      .range([height, 0]);
    
    // 添加Y轴
    svg.append("g")
      .call(d3.axisLeft(y));
    
    // 添加Y轴标签
    svg.append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", -40)
      .attr("x", -height/2)
      .style("text-anchor", "middle")
      .text("概率分值");
    
    // 为每个系列创建折线
    series.forEach((s: any, i: number) => {
      // 确保数据存在
      if (!s.data || s.data.length === 0) return;
      
      // 将数据转换为点
      const points: DataPoint[] = xValues.map((x: number, j: number) => ({
        x: x,
        y: s.data[j] || 0
      }));
      
      // 创建线生成器，使用曲线插值
      const line = d3.line<DataPoint>()
        .x(d => x(d.x))
        .y(d => y(d.y))
        .curve(d3.curveBasis); // 使用平滑曲线
      
      // 添加线条路径
      svg.append("path")
        .datum(points)
        .attr("fill", "none")
        .attr("stroke", s.color || d3.schemeCategory10[i % 10])
        .attr("stroke-width", 2)
        .attr("d", line);
    });
    
    // 添加图例
    const legend = svg.append("g")
      .attr("transform", `translate(${width + 10}, 0)`);
    
    series.forEach((s: any, i: number) => {
      const legendRow = legend.append("g")
        .attr("transform", `translate(0, ${i * 20})`);
      
      legendRow.append("rect")
        .attr("width", 10)
        .attr("height", 10)
        .attr("fill", s.color || d3.schemeCategory10[i % 10]);
      
      legendRow.append("text")
        .attr("x", 15)
        .attr("y", 9)
        .text(s.name || `系列 ${i+1}`)
        .style("font-size", "12px");
    });
  };

  // 渲染序列和注释上下对齐
  const renderSequenceWithAnnotation = () => {
    // 如果没有序列或摘要，显示提示
    if (!sequence) {
      return (
        <div>
          <Text type="warning">无序列数据可显示</Text>
        </div>
      );
    }
    
    // 创建注释带
    const regionBands: RegionBand[] = summary.map((region: any) => {
      // 确保region.type是一个有效的RegionType
      const regionType = (region.type || 'outside') as RegionType;
      const color = regionColors[regionType] || '#aaaaaa';
      
      return {
        start: region.start || 1,
        end: region.end || sequence.length,
        type: regionType,
        color: color
      };
    });
    
    // 按位置对残基着色
    const coloredSequence = sequence.split('').map((aa: string, idx: number) => {
      // 找到这个位置属于哪个区域
      const position = idx + 1; // 1-based indexing
      const region = regionBands.find((r: RegionBand) => position >= r.start && position <= r.end);
      
      return (
        <Tooltip key={idx} title={region ? `${region.type} (${position})` : `位置 ${position}`}>
          <Text
            style={{
              color: region ? region.color : '#000000',
              fontWeight: region && region.type === 'transmembrane helix' ? 'bold' : 'normal',
              fontSize: '1.1em',
            }}
          >
            {aa}
          </Text>
        </Tooltip>
      );
    });

    // 创建注释条带图
    const createAnnotationBars = () => {
      if (regionBands.length === 0) {
        return <Text type="warning">未检测到跨膜区域</Text>;
      }
      
      return (
        <div style={{ height: 40, position: 'relative', marginBottom: 8 }}>
          {regionBands.map((region: RegionBand, idx: number) => {
            const widthPercentage = (region.end - region.start + 1) / sequence.length * 100;
            const startPercentage = (region.start - 1) / sequence.length * 100;
            
            return (
              <Tooltip key={idx} title={`${region.type} (${region.start}-${region.end})`}>
                <div
                  style={{
                    position: 'absolute',
                    left: `${startPercentage}%`,
                    width: `${widthPercentage}%`,
                    height: '100%',
                    backgroundColor: region.color,
                    opacity: 0.7,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: '#fff',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {region.type.substring(0, 3).toUpperCase()}
                </div>
              </Tooltip>
            );
          })}
        </div>
      );
    };

    return (
      <div style={{ marginBottom: 24 }}>
        {createAnnotationBars()}
        <div style={{ 
          fontFamily: 'monospace', 
          lineHeight: '2', 
          whiteSpace: 'pre-wrap', 
          wordBreak: 'break-all' 
        }}>
          {coloredSequence}
        </div>
      </div>
    );
  };

  // 渲染区域摘要表格
  const renderRegionSummary = () => {
    // 如果没有区域信息，不显示表格
    if (summary.length === 0) {
      return (
        <Text type="warning">未检测到任何区域信息</Text>
      );
    }
    
    const columns = [
      {
        title: '区域类型',
        dataIndex: 'type',
        key: 'type',
        render: (text: string) => {
          // 确保text是一个有效的RegionType
          const regionType = (text || 'outside') as RegionType;
          return (
            <Tag color={regionColors[regionType] || 'default'}>
              {text}
            </Tag>
          );
        }
      },
      {
        title: '起始位置',
        dataIndex: 'start',
        key: 'start',
      },
      {
        title: '结束位置',
        dataIndex: 'end',
        key: 'end',
      },
      {
        title: '长度',
        dataIndex: 'length',
        key: 'length',
        render: (_: any, record: any) => record.end - record.start + 1
      },
      {
        title: '氨基酸序列',
        dataIndex: 'sequence',
        key: 'sequence',
        render: (text: string) => (
          <Text style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {text}
          </Text>
        )
      }
    ];

    // 扩展摘要数据，添加序列信息
    const summaryWithSequence = summary.map((region: any) => ({
      ...region,
      sequence: sequence.substring((region.start - 1) || 0, region.end || sequence.length)
    }));

    return (
      <Table 
        dataSource={summaryWithSequence} 
        columns={columns} 
        rowKey={(record, index) => `${record.type || 'region'}-${record.start || 0}-${record.end || 0}-${index}`}
        pagination={false}
        size="small"
      />
    );
  };

  // 渲染统计信息
  const renderSummaryInfo = () => {
    // 计算跨膜区数量
    const tmCount = summary.filter((region: any) => 
      region.type === 'transmembrane helix'
    ).length;
    
    return (
      <Card style={{ marginBottom: 24 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text strong>蛋白质名称: </Text>
            <Text>{sequenceId || processedData?.name || "未命名蛋白质"}</Text>
          </div>
          <div>
            <Text strong>序列长度: </Text>
            <Text>{sequence.length} 氨基酸</Text>
          </div>
          <div>
            <Text strong>检测到的跨膜区数量: </Text>
            <Text>{tmCount}</Text>
            {tmCount > 0 ? 
              <Tag color="green" style={{ marginLeft: 8 }}>跨膜蛋白</Tag> : 
              <Tag color="blue" style={{ marginLeft: 8 }}>非跨膜蛋白</Tag>
            }
          </div>
          {processedData?.tm_protein_probability !== undefined && (
            <div>
              <Text strong>跨膜蛋白概率: </Text>
              <Text>{(processedData.tm_protein_probability * 100).toFixed(2)}%</Text>
            </div>
          )}
        </Space>
      </Card>
    );
  };

  // 渲染图例
  const renderLegend = () => {
    return (
      <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {Object.entries(regionColors).map(([type, color]) => (
          <Tag key={type} color={color}>
            {type}
          </Tag>
        ))}
      </div>
    );
  };

  return (
    <BaseResultDisplay
      data={data}
      title={title || "跨膜区预测结果"}
      onDeleteResult={onDeleteResult}
      multiResults={multiResults}
      showRawData={false}
    >
      {renderSummaryInfo()}
      
      <Title level={5}>序列与区域注释</Title>
      {sequenceId && (
        <Paragraph>
          <Text strong>序列ID: </Text>
          <Text>{sequenceId}</Text>
        </Paragraph>
      )}
      {renderLegend()}
      {renderSequenceWithAnnotation()}
      
      <Divider />
      
      <Title level={5}>区域信息</Title>
      {renderRegionSummary()}
      
      {xValues.length > 0 && series.length > 0 && (
        <>
          <Divider />
          <Title level={5}>跨膜概率曲线</Title>
          <div ref={chartRef} style={{ width: '100%', height: '300px' }} />
        </>
      )}
    </BaseResultDisplay>
  );
};

export default TMPredictionResult;